import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useSuspenseQuery, queryOptions } from "@tanstack/react-query";
import {
  ArrowLeft,
  BookOpen,
  Download,
  ExternalLink,
  Search,
  ShieldCheck,
} from "lucide-react";
import { getActFullText } from "@/lib/api/legislation.functions";

const actQuery = (slug: string) =>
  queryOptions({
    queryKey: ["legislation-full", slug],
    queryFn: () => getActFullText({ data: { slug } }),
    staleTime: 1000 * 60 * 30,
  });

export const Route = createFileRoute("/legislation/$slug")({
  loader: async ({ context, params }) => {
    const res = await context.queryClient.ensureQueryData(actQuery(params.slug));
    if (!res.ok || !res.act) throw notFound();
    return res;
  },
  head: ({ loaderData, params }) => {
    const act = loaderData?.act;
    const t = act?.title ?? "Act";
    const url = `https://nkadimengdannylcs.com/legislation/${params.slug}`;
    return {
      meta: [
        { title: `${t} — Full text | NDLCS Legislation Library` },
        {
          name: "description",
          content: act
            ? `${t} (Act ${act.number}) — ${act.summary.slice(0, 150)}`
            : `Read the full text of ${t} on the NDLCS legislation library.`,
        },
        { property: "og:title", content: `${t} — Full text` },
        {
          property: "og:description",
          content: act
            ? `${t} (Act ${act.number}). ${act.summary.slice(0, 200)}`
            : `Plain-language summary and full text of ${t}, cached from the official gov.za publication.`,
        },
        { property: "og:type", content: "article" },
        { property: "og:url", content: url },
      ],
      links: [{ rel: "canonical", href: url }],
      scripts: act
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Legislation",
                name: act.title,
                alternateName: act.short ?? undefined,
                legislationIdentifier: `Act ${act.number}`,
                legislationJurisdiction: "ZA",
                legislationType: "Act",
                url,
                sameAs: act.url ?? undefined,
                about: act.category,
                description: act.summary,
                publisher: {
                  "@type": "GovernmentOrganization",
                  name: "Republic of South Africa",
                },
              }),
            },
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                itemListElement: [
                  {
                    "@type": "ListItem",
                    position: 1,
                    name: "Legislation Library",
                    item: "https://nkadimengdannylcs.com/legislation",
                  },
                  {
                    "@type": "ListItem",
                    position: 2,
                    name: act.title,
                    item: url,
                  },
                ],
              }),
            },
          ]
        : undefined,
    };
  },
  errorComponent: ({ error, reset }) => (
    <div className="container-narrow py-16">
      <h1 className="font-display text-2xl text-navy">Something went wrong</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error.message}</p>
      <button
        onClick={reset}
        className="mt-4 rounded-md bg-navy px-4 py-2 text-sm text-navy-foreground"
      >
        Try again
      </button>
    </div>
  ),
  notFoundComponent: () => (
    <div className="container-narrow py-16">
      <h1 className="font-display text-2xl text-navy">Act not found</h1>
      <Link to="/legislation" className="mt-3 inline-block text-navy underline">
        Back to the library
      </Link>
    </div>
  ),
  component: ActReader,
});

const PAGE_SIZE = 40; // paragraphs per page

function ActReader() {
  const { slug } = Route.useParams();
  const { data } = useSuspenseQuery(actQuery(slug));
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);

  const paragraphs = useMemo(() => {
    if (!data.fullText) return [] as string[];
    return data.fullText
      .split(/\n{1,}/)
      .map((p) => p.trim())
      .filter((p) => p.length > 0);
  }, [data.fullText]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return paragraphs;
    return paragraphs.filter((p) => p.toLowerCase().includes(q));
  }, [paragraphs, query]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const visible = filtered.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE,
  );

  function highlight(text: string) {
    const q = query.trim();
    if (!q) return text;
    const re = new RegExp(
      `(${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
      "gi",
    );
    const parts = text.split(re);
    return parts.map((part, i) =>
      re.test(part) ? (
        <mark key={i} className="rounded bg-gold/30 px-0.5">
          {part}
        </mark>
      ) : (
        <span key={i}>{part}</span>
      ),
    );
  }

  const act = data.act!;

  return (
    <div className="bg-background">
      <section className="border-b border-border bg-navy/5 py-10">
        <div className="container-narrow">
          <Link
            to="/legislation"
            className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Legislation Library
          </Link>
          <p className="mt-4 text-xs uppercase tracking-[0.3em] text-gold">
            {act.category}
          </p>
          <h1 className="mt-2 font-display text-3xl text-navy md:text-4xl">
            {act.title}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Act No. {act.number}
            {act.short && <> · {act.short}</>}
          </p>
          <p className="mt-4 max-w-3xl text-sm text-foreground/80">
            {act.summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <a
              href={`/api/public/legislation?slug=${act.slug}&full=1`}
              className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground hover:bg-navy/90"
            >
              <Download className="h-3.5 w-3.5" /> Full Act (PDF)
            </a>
            <a
              href={`/api/public/legislation?slug=${act.slug}`}
              className="inline-flex items-center gap-1.5 rounded-md border border-navy px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
            >
              <Download className="h-3.5 w-3.5" /> Fact sheet (PDF)
            </a>
            {act.url && (
              <a
                href={act.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs font-medium text-navy hover:underline"
              >
                Official source on gov.za{" "}
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {data.servedFrom !== "none" && (
              <span className="ml-auto text-[11px] text-muted-foreground">
                Served from {data.servedFrom === "cache" ? "cache" : "gov.za"}
              </span>
            )}
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="container-narrow">
          {!data.fullText && (
            <div className="rounded-lg border border-border bg-card p-6">
              <h2 className="flex items-center gap-2 font-display text-lg text-navy">
                <BookOpen className="h-5 w-5 text-gold" /> Full text not
                available on-site yet
              </h2>
              <p className="mt-2 text-sm text-muted-foreground">
                {data.error
                  ? `We couldn't retrieve the full text automatically: ${data.error}.`
                  : "This Act's full text has not been cached yet."}{" "}
                Please use the official gov.za link above or download the
                fact-sheet PDF while we curate a copy.
              </p>
            </div>
          )}

          {data.fullText && (
            <>
              <div className="mb-6 flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-4">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => {
                      setQuery(e.target.value);
                      setPage(1);
                    }}
                    placeholder="Search within this Act…"
                    className="w-full rounded-md border border-input bg-background pl-10 pr-3 py-2 text-sm focus:border-navy focus:outline-none focus:ring-1 focus:ring-navy"
                    aria-label="Search within this Act"
                  />
                </div>
                <span className="text-xs text-muted-foreground">
                  {filtered.length} paragraph
                  {filtered.length === 1 ? "" : "s"}
                  {query.trim() ? " match" : ""} · Page {safePage} of{" "}
                  {totalPages}
                </span>
              </div>

              <article className="prose prose-sm max-w-none rounded-lg border border-border bg-card p-6 leading-relaxed">
                {visible.length === 0 ? (
                  <p className="text-muted-foreground">
                    No paragraphs match your search.
                  </p>
                ) : (
                  visible.map((p, i) => {
                    const isHeading =
                      /^(CHAPTER|PART|SCHEDULE|SECTION)\b/i.test(p) &&
                      p.length < 140;
                    if (isHeading) {
                      return (
                        <h3
                          key={`${safePage}-${i}`}
                          className="mt-6 font-display text-base font-semibold text-navy"
                        >
                          {highlight(p)}
                        </h3>
                      );
                    }
                    return (
                      <p
                        key={`${safePage}-${i}`}
                        className="mt-3 text-sm text-foreground/85"
                      >
                        {highlight(p)}
                      </p>
                    );
                  })
                )}
              </article>

              {totalPages > 1 && (
                <nav
                  className="mt-6 flex items-center justify-between gap-4"
                  aria-label="Pagination"
                >
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={safePage <= 1}
                    className="rounded-md border border-input px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs text-muted-foreground">
                    Page {safePage} of {totalPages}
                  </span>
                  <button
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={safePage >= totalPages}
                    className="rounded-md border border-input px-4 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    Next →
                  </button>
                </nav>
              )}

              <p className="mt-6 text-[11px] text-muted-foreground">
                Text is reproduced from the official gov.za publication for
                educational purposes. Always verify against the current gazetted
                version before relying on it.
              </p>
            </>
          )}
        </div>
      </section>

      <section className="bg-navy/5 py-10">
        <div className="container-narrow rounded-2xl border border-border bg-card p-6 text-center">
          <ShieldCheck className="mx-auto h-7 w-7 text-gold" />
          <h2 className="mt-3 font-display text-xl text-navy">
            Need help applying this Act?
          </h2>
          <Link
            to="/book"
            className="mt-4 inline-flex items-center justify-center rounded-md bg-navy px-4 py-2 text-sm text-navy-foreground hover:bg-navy/90"
          >
            Book a consultation
          </Link>
        </div>
      </section>
    </div>
  );
}
